import AuthorPageComponent from '@bridebook/web/app-basic/components/author-page/author-page-component/author-page-component';
import { Breadcrumbs } from '@bridebook/web/app-basic/components/breadcrumbs/breadcrumbs.component';
import Footer from '@bridebook/web/app-basic/components/footer/footer';
import Head from '@bridebook/web/app-basic/components/head/head';
import { TopBar } from '@bridebook/web/app-basic/components/header/top-bar.component';
import { setArticlesSupport } from '@bridebook/web/app-basic/lib/article/article-slice';
import {
  EdgeCacheTTL,
  basicGSSPWrapper,
} from '@bridebook/web/app-basic/lib/get-basic-server-side-props-wrapper';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { IArticlesRequestResponse } from '@bridebook/toolbox/src/articles/types';
import LoadingCircle from '@bridebook/ui/src/components/bbcommon/loading-circle/loading-circle';
import { getArticlesPromise } from '@bridebook/web/app-shared/lib/articles/get-articles';
import { getArticlesMarketSupport } from '@bridebook/web/app-shared/lib/articles/get-articles-market-support';
import { authorSchema } from '@bridebook/web/app-shared/lib/articles/schema';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { IBreadcrumbItem } from '@bridebook/web/app-shared/lib/types';
import { AppType } from '@bridebook/web/pages/_app';
import { UrlHelper } from '@bridebook/web/lib/url-helper';
import { useAuthorMeta } from '@bridebook/web/app-shared/lib/articles/use-author-meta';
import { AUTHOR_ARTICLES_PER_PAGE } from '@bridebook/web/app-shared/lib/articles/constants';
import { Flex } from '@bridebook/aura';

interface StaticProps {
  content: IArticlesRequestResponse;
  currentPage: number;
}

const AuthorPage = ({ content, currentPage }: StaticProps) => {
  const router = useRouter();
  const market = useMarket();
  const { t } = useTranslation(['articles', 'common']);
  const authorDetails = content?.articles && content?.articles[0].post_author;
  const articles = content?.articles;
  const breadcrumbsData = [
    {
      name: t('common:breadcrumbs.articles.advice'),
      path: UrlHelper.advice,
      id: 'advice',
    },
    {
      name: t('author-page.title'),
      path: `/${market.prefix}${UrlHelper.author.nameSurname(router.query.nameSurname as string)}`,
      id: 'author-page',
    },
  ] as IBreadcrumbItem[];

  const { metaTitle, metaDescription, ogImage } = useAuthorMeta(authorDetails);

  return authorDetails ? (
    <>
      <Head
        title={metaTitle}
        plainTitle
        plainOgTitle
        description={metaDescription}
        ogTitle={metaTitle}
        ogDescription={metaDescription}
        ogImage={ogImage}
        isArticle>
        {metaTitle && <meta name="twitter:title" content={metaTitle} />}
        {metaDescription && <meta name="twitter:description" content={metaDescription} />}
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(authorSchema(authorDetails, market), null, 2),
          }}
        />
      </Head>
      <TopBar />
      <div className="mt-2">
        <Breadcrumbs showAlways breadcrumbsData={breadcrumbsData} />
        <AuthorPageComponent
          authorDetails={authorDetails}
          articles={articles}
          recordsFound={content?.recordsFound}
          currentPage={currentPage}
        />
      </div>
      <Footer />
    </>
  ) : (
    <Flex className="mt-12 w-full items-center">
      <LoadingCircle size={120} color="black" animate />
    </Flex>
  );
};

export const getServerSideProps = basicGSSPWrapper(
  ['articles'],
  async ({ store, context, initialAppState }) => {
    const { params } = context;
    const { market } = initialAppState;
    const currentPage = Number(context.query.page) || 1;
    const [firstName = '', lastName = ''] = (params?.nameSurname as string).split('-');

    const articlesSupport = await getArticlesMarketSupport(market.prefix);
    store.dispatch(setArticlesSupport(articlesSupport));

    return {
      props: {
        content: await getArticlesPromise({
          category: market.prefix,
          searchString: '',
          tag: '',
          resultsPerPage: AUTHOR_ARTICLES_PER_PAGE,
          pageNum: currentPage,
          authorFirstName: firstName,
          authorLastName: lastName,
        }),
        currentPage,
      },
    };
  },
  {
    vercelCacheControl: {
      maxAge: EdgeCacheTTL.WEEK,
      sMaxAge: EdgeCacheTTL.WEEK,
      staleWhileRevalidate: EdgeCacheTTL.MONTH,
    },
    cloudflareCacheControl: {
      maxAge: EdgeCacheTTL.DAY,
      sMaxAge: EdgeCacheTTL.DAY,
      staleWhileRevalidate: EdgeCacheTTL.WEEK,
    },
  },
);

AuthorPage.AppType = AppType.Basic;

export default AuthorPage;
